import type {
  AdminOrderDetailDto,
  AdminOrderStatus,
  AdminRefundPolicy,
  ListAdminOrdersResultDto,
} from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin order-management surface (AUT-123 /
 * ADMIN-11), targeting the AUT-112 backend:
 *
 *   - `GET  /admin/orders`                  (cursor-paginated)
 *   - `GET  /admin/orders/:id`
 *   - `POST /admin/orders/:id/force-cancel`
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list   → `['admin', 'orders', 'list', filters]`
 *   detail → `['admin', 'orders', 'detail', id]`
 * The force-cancel mutation invalidates by the `['admin', 'orders']`
 * prefix on success so the list + the open detail refetch.
 *
 * Mirrors `admin-users.ts`, with ONE deliberate divergence (architect 🔴):
 * force-cancel is a `POST`, NOT a `PATCH` — the users status mutation is a
 * PATCH; copying that verb here would 405 at runtime.
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const ORDERS_BASE = '/admin/orders';

/** Stable query-key prefix; the force-cancel mutation invalidates against this. */
export const ADMIN_ORDERS_KEY = ['admin', 'orders'] as const;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminOrdersQuerySchema` — `status` is a multi-select; `q` is a
 * min-3-char free-text search over orderNumber + customer phone (enforced
 * client-side before it reaches here); `from` / `to` are ISO date strings.
 * `cursor` / `limit` drive the cursor pagination.
 */
export interface AdminOrdersListParams {
  status?: AdminOrderStatus[];
  q?: string;
  from?: string;
  to?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminOrdersListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.status && params.status.length > 0) query.status = params.status.join(',');
  if (params.q && params.q.length > 0) query.q = params.q;
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminOrdersList = (
  params: AdminOrdersListParams,
): UseQueryResult<ListAdminOrdersResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_ORDERS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminOrdersResultDto>(ORDERS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminOrderDetail = (
  id: string | null,
): UseQueryResult<AdminOrderDetailDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_ORDERS_KEY, 'detail', id],
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminOrderDetailDto>(`${ORDERS_BASE}/${id}`, { signal });
      return data;
    },
  });

/**
 * Envelope returned by `POST /admin/orders/:id/force-cancel`. `dto` is the
 * USER-facing `OrderDto` (a different shape from `AdminOrderListItemDto` /
 * `AdminOrderDetailDto`) — the SPA does NOT read it to optimistic-patch a
 * list row; it just triggers a cache invalidation so the list + detail
 * refetch the admin projections. `previousStatus` is a backend audit
 * concern (read by the `@Audit` interceptor) — the SPA ignores it.
 *
 * Typed minimally (`unknown` for the order body) because the user-facing
 * `OrderDto` is api-internal and never imported into the admin SPA.
 */
interface ForceCancelResponse {
  dto: unknown;
  previousStatus: string;
}

export interface ForceCancelVariables {
  id: string;
  reason: string;
  refundPolicy: AdminRefundPolicy;
}

/**
 * Low-level force-cancel mutation. Role + permission + audit gated
 * server-side (AUT-138 removed per-action TOTP step-up).
 *
 * 🔴 architect: this is a `POST`, NOT a `PATCH` — a PATCH would 405.
 */
const postForceCancel = async (variables: ForceCancelVariables): Promise<void> => {
  const { id, reason, refundPolicy } = variables;
  await apiClient.post<ForceCancelResponse>(`${ORDERS_BASE}/${id}/force-cancel`, {
    reason,
    refundPolicy,
  });
};

/**
 * Hook factory binding the force-cancel mutation to an action callback. On
 * success the caller invalidates the `['admin','orders']` prefix (via
 * `invalidate`) so the list + the open detail refetch — the `OrderDto`
 * envelope is intentionally NOT used to patch the row (its shape differs
 * from the admin projections; architect guardrail).
 *
 * Returns `{ action, invalidate }`:
 *   - `action` runs the force-cancel POST.
 *   - `invalidate` is the cache-invalidation callback the caller runs after
 *     the action resolves.
 */
export const useForceCancelAction = (): {
  action: (variables: ForceCancelVariables) => Promise<void>;
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    action: postForceCancel,
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_ORDERS_KEY }),
  };
};
